import Big from 'big.js';
import getConfig from './config';
import db from '../store/RefDatabase';
import {
  toNonDivisibleNumber,
  scientificNotationToString,
  calculateMarketPrice,
} from '../utils/numbers';
import { TokenMetadata, ftGetTokenMetadata } from './ft-contract';
import {
  getAllPoolsByTokens,
  getAllStablePoolsFromCacheForServer,
  Pool,
} from './pool';
import { isStablePool } from '../services/near';
import { getTokenPriceList } from '../services/indexer';
export interface IEstimateSwapServerView {
  amount_in: string;
  amount_out: string;
  contract_in: string;
  contract_out: string;
  routes: IServerRoute[];
  contract?: string;
}
export interface IServerRoute {
  amount_in: string;
  min_amount_out: string;
  pools: IServerPool[];
  tokens?: TokenMetadata[];
}
interface IServerPool {
  amount_in: string;
  min_amount_out: string;
  pool_id: string | number;
  token_in: string;
  token_out: string;
}

export const estimateSwapFromServer = async ({
  tokenIn,
  tokenOut,
  amountIn,
  slippage,
  supportLedger,
}) => {
  const timeoutDuration = 5000;
  const controller = new AbortController();
  const timeOutId = setTimeout(() => {
    controller.abort();
  }, timeoutDuration);
  const resultFromServer = await fetch(
    `${getConfig().smartRouterUrl}/findPath?amountIn=${amountIn}&tokenIn=${
      tokenIn.id
    }&tokenOut=${tokenOut.id}&pathDeep=${supportLedger ? 1 : 3}&slippage=${
      Number(slippage) / 100
    }`,
    {
      signal: controller.signal,
    }
  )
    .then((response) => {
      return response.json();
    })
    .finally(() => {
      clearTimeout(timeOutId);
    });
  return resultFromServer;
};

export async function getAvgFeeFromServer({
  estimatesFromServer,
  setAvgFee,
  tokenInAmount,
  tokenIn,
  poolsMap,
}: {
  tokenInAmount: string;
  tokenIn: TokenMetadata;
  estimatesFromServer: IEstimateSwapServerView;
  setAvgFee: (fee: number) => void;
  poolsMap: Record<string, Pool>;
}) {
  let avgFee: number = 0;
  const { routes } = estimatesFromServer;
  routes.forEach((route) => {
    const { amount_in, pools } = route;
    const allocation = new Big(amount_in).div(
      new Big(toNonDivisibleNumber(tokenIn.decimals, tokenInAmount))
    );
    const routeFee = pools.reduce((totalFee, routePool) => {
      return totalFee.plus(poolsMap[routePool.pool_id]?.fee || 0);
    }, new Big(0));
    avgFee += allocation.mul(routeFee).toNumber();
  });
  setAvgFee(avgFee);
}
export async function getUsedPools(
  routes: IServerRoute[],
  needCheckExpiration?: boolean
) {
  const { topPools, stablePools } = await getAllPoolsFromCache(
    needCheckExpiration
  );
  const pools: Record<string, Pool> = {};
  routes.forEach((route) => {
    route.pools.forEach((routePool) => {
      let pool;
      pool = topPools.find(
        (candidatePool) => +candidatePool.id === +routePool.pool_id
      );
      if (!pool) {
        pool = stablePools.find(
          (candidatePool) => +candidatePool.id === +routePool.pool_id
        );
      }
      if (pool) {
        pools[pool.id] = pool;
      }
    });
  });
  return pools;
}
export async function getUsedTokens(routes: IServerRoute[]) {
  const pending = routes.map((route) => getTokensOfRoute(route));
  const tokensList = await Promise.all(pending);
  const allTokens = tokensList.flat();
  const tokens: Record<string, TokenMetadata> = allTokens.reduce(
    (tokensById, token) => {
      tokensById[token.id] = token;
      return tokensById;
    },
    {}
  );
  return tokens;
}
export async function getTokensOfRoute(route: IServerRoute) {
  const tokenIds = route.pools.reduce((tokenIdList, routePool, index) => {
    if (index == 0) {
      tokenIdList.push(routePool.token_in, routePool.token_out);
    } else {
      tokenIdList.push(routePool.token_out);
    }
    return tokenIdList;
  }, []);
  const pending = tokenIds.map((tokenId) => ftGetTokenMetadata(tokenId));
  const tokens = await Promise.all(pending);
  return tokens as TokenMetadata[];
}
export async function getAllPoolsFromCache(needCheckExpiration?: boolean) {
  const { filteredPools: topPools } = await getAllPoolsByTokens(
    needCheckExpiration
  );
  const { allStablePools } = await getAllStablePoolsFromCacheForServer();
  const topPoolsMap = topPools.reduce((poolsById, pool) => {
    poolsById[pool.id] = pool;
    return poolsById;
  }, {});
  const stablePoolsMap = allStablePools.reduce((poolsById, pool) => {
    poolsById[pool.id] = pool;
    return poolsById;
  }, {});
  return {
    topPools,
    stablePools: allStablePools,
    poolsMap: { ...topPoolsMap, ...stablePoolsMap },
  };
}
export async function getPriceImpactFromServer({
  estimatesFromServer,
  tokenIn,
  tokenOut,
  tokenInAmount,
  tokenOutAmount,
  tokenPriceList,
  setPriceImpactValue,
  poolsMap,
  tokensMap,
}: {
  estimatesFromServer: IEstimateSwapServerView;
  tokenInAmount: string;
  tokenIn: TokenMetadata;
  tokenOut: TokenMetadata;
  tokenOutAmount: string;
  tokenPriceList: any;
  setPriceImpactValue: (impact: string) => void;
  poolsMap: Record<string, Pool>;
  tokensMap: Record<string, TokenMetadata>;
}) {
  try {
    const newPrice = new Big(tokenInAmount || '0').div(
      new Big(tokenOutAmount || '1')
    );
    const { routes } = estimatesFromServer;
    const priceIn = tokenPriceList[tokenIn.id]?.price;
    const priceOut = tokenPriceList[tokenOut.id]?.price;
    const priceImpactForRoutes = routes.map((route) => {
      let oldPrice: Big;
      if (!!priceIn && !!priceOut) {
        oldPrice = new Big(priceOut).div(new Big(priceIn));

        return newPrice.lt(oldPrice)
          ? '0'
          : newPrice.minus(oldPrice).div(newPrice).times(100).abs().toFixed();
      }
      const pools = route.pools.map((pool) => poolsMap[pool.pool_id]);
      oldPrice = pools.reduce((price, pool, index) => {
        const curRate = isStablePool(pool.id)
          ? new Big(pool.rates[route.pools[index].token_out]).div(
              new Big(pool.rates[route.pools[index].token_in])
            )
          : new Big(
              scientificNotationToString(
                calculateMarketPrice(
                  pool,
                  tokensMap[route.pools[index].token_in],
                  tokensMap[route.pools[index].token_out]
                ).toString()
              )
            );

        return price.mul(curRate);
      }, new Big(1));
      return newPrice.lt(oldPrice)
        ? '0'
        : newPrice.minus(oldPrice).div(newPrice).times(100).abs().toFixed();
    });
    const rawRes = priceImpactForRoutes.reduce(
      (pre, routePriceImpact, index) => {
        return pre.plus(
          new Big(routes[index].amount_in)
            .div(new Big(toNonDivisibleNumber(tokenIn.decimals, tokenInAmount)))
            .mul(routePriceImpact)
        );
      },

      new Big(0)
    );
    setPriceImpactValue(scientificNotationToString(rawRes.toString()));
  } catch (error) {
    setPriceImpactValue('0');
  }
}
export const getTokenPriceListFromCacheForServer = async () => {
  let prices;
  prices = await db.queryTokenPrices();
  const validCache = await db.checkTokenPrices();
  if (!prices?.length || !validCache) {
    prices = await getTokenPriceList();
    db.cacheTokenPrices(prices);
  }
  const map = prices?.reduce((pricesByTokenId, tokenPrice) => {
    return {
      ...pricesByTokenId,
      [tokenPrice.id]: tokenPrice,
    };
  }, {});
  return map || {};
};
